import { useCallback, useState } from 'react';

import { CirclePlusIcon, Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';

import {
  AddCustomBillingEntityCommand,
  AddCustomBillingEntityCommandProps,
  BillingEntityCandidate,
} from './add-custom-billing-entity-command';

type AddCustomEntityButtonProps<T extends BillingEntityCandidate> = Omit<
  AddCustomBillingEntityCommandProps<T>,
  'open' | 'onOpenChange'
> & {
  buttonLabel: string;
  /** Explains what the picker is for — shown on hover over the info icon next to the button. */
  buttonTooltip?: string;
  disabled?: boolean;
};

/**
 * "Include an entity that is not in the table yet" affordance: an info icon, a button, and the
 * billing-entity picker dialog it opens. Selecting an item hands it to `onSelect`; the caller
 * appends the row.
 */
export function AddCustomEntityButton<T extends BillingEntityCandidate>({
  buttonLabel,
  buttonTooltip,
  disabled,
  ...commandProps
}: AddCustomEntityButtonProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);

  return (
    <>
      <div className="flex items-center gap-2">
        {buttonTooltip && (
          <SimpleTooltip content={buttonTooltip} contentClassName="max-w-xs">
            <button
              type="button"
              aria-label={buttonTooltip}
              className="inline-flex text-muted-foreground outline-none hover:text-foreground"
            >
              <Info className="size-4" />
            </button>
          </SimpleTooltip>
        )}
        <Button variant="outline" size="sm" onClick={open} disabled={disabled}>
          <CirclePlusIcon className="size-4" />
          {buttonLabel}
        </Button>
      </div>

      <AddCustomBillingEntityCommand {...commandProps} open={isOpen} onOpenChange={setIsOpen} />
    </>
  );
}
